import { useState } from "react";
import { TrashIcon, Edit3Icon, RotateCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";

import BreadCrumb from "../../../Components/BreadCrumb";
import TableListing from "../../../Components/TableListing";
import { delay, ToastMessage, useDebounce } from "../../../Components/Utils";
import { Button } from "../../../Components/Buttons";
import { ROUTE_PATHS } from "../../../constants/routeConstants";
import { handleError } from "../../../stores/components/handleError";

import payloadHelper from "../../../stores/api/helpers/payload.helper";
import { useBulkDeleteReferrelPatientsMutation, useBulkDestroyReferrelPatientsMutation, useBulkRestoreReferrelPatientsMutation, useDeletePatientReferralMutation, useDestroyPatientReferralMutation, useFetchReferrelPatientsQuery, useFetchTrashedReferrelPatientsQuery, useRestorePatientReferralMutation } from "../../../stores/api/patient.referrel.api";

const EMPTY_PAGINATION = {
  data: [],
  current_page: 1,
  total_pages: 1,
  total_items: 0,
  current_items: 0,
  limit: 10,
};

const PatientReferralList = () => {
  const navigate = useNavigate();
  const [trashed, setTrashed] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const debouncedSearch = useDebounce(search, 500)
  const queryParams = payloadHelper.forList({
    page,
    limit,
    search: debouncedSearch,
    sortBy: sortConfig.key,
    sortOrder: sortConfig.direction,
  });

  const trashedQuery = useFetchTrashedReferrelPatientsQuery(queryParams, { skip: !trashed });
  const activeQuery = useFetchReferrelPatientsQuery(queryParams, { skip: trashed });
  const { data: patientReferrals = EMPTY_PAGINATION, isFetching } = trashed ? trashedQuery : activeQuery;

  const [deletePatientReferral] = useDeletePatientReferralMutation();
  const [destroyPatientReferral] = useDestroyPatientReferralMutation();
  const [restorePatientReferral] = useRestorePatientReferralMutation();
  const [bulkDeletePatientReferral] = useBulkDeleteReferrelPatientsMutation();
  const [bulkDestroyPatientReferral] = useBulkDestroyReferrelPatientsMutation();
  const [bulkRestorePatientReferral] = useBulkRestoreReferrelPatientsMutation();

  const resetPage = () => setPage(1);

  const handleSingle = (row, type = "delete") => {
    const verb = type === "restore" ? "restore" : "delete";
    ToastMessage({
      type: "warning",
      message: `You sure you wanna ${verb} this Patient Referrel?`,
      title: `Patient Referrel ${verb === "delete" ? "Delete" : "Restore"}..!`,
      toastType: "confirm",
      onYes: async () => {
        try {
          if (type === "restore") {
            await restorePatientReferral(row.id).unwrap();
          } else if (type === "destroy") {
            await destroyPatientReferral(row.id).unwrap();
          } else {
            await deletePatientReferral(row.id).unwrap();
          }
          ToastMessage({
            type: "success",
            title: `Patient Referrel ${type === "restore" ? "Restored" : "Deleted"}!`,
            message: `${type === "restore" ? "Restored" : "Deleted"} Patient Referrel: ${row?.patient?.patient_name}`,
          });
        } catch (err) {
          handleError(err, "Failed to update user.");
        }
      },
    });
  };

  const handleMultiple = (selectedItems, type = "delete") => {
    const verb = type === "restore" ? "restore" : "delete";
    ToastMessage({
      type: "warning",
      message: `You sure you wanna ${verb} these Patient Referrals?`,
      title: `Patient Referrals ${verb === "delete" ? "Delete" : "Restore"}..!`,
      toastType: "confirm",
      onYes: async () => {
        try {
          const body = payloadHelper.forBulk(selectedItems);
          if (type === "restore") {
            await bulkRestorePatientReferral(body).unwrap();
          } else if (type === "destroy") {
            await bulkDestroyPatientReferral(body).unwrap();
          } else {
            await bulkDeletePatientReferral(body).unwrap();
          }
          ToastMessage({
            type: "success",
            title: `${type === "restore" ? "Restored" : "Deleted"} selected units`,
            message: `${selectedItems.length} Patient Referrels ${type === "restore" ? "restored" : "deleted"} successfully.`,
          });
        } catch (err) {
          handleError(err, "Failed bulk action.");
        }
      },
    });
  };

  const handleTrashedChange = (value) => {
    setTrashed(value);
    resetPage();
  };

  const headers = [
    {
      key: "patient.system_number", label: "Patient-ID / CNIC", sortable: true, render: (row) => (
        <div className="text-xs whitespace-nowrap">
          {row?.patient?.system_number || row?.patient?.patient_cnic}
        </div>)
    },
    { key: "patient.patient_name", label: "Name", sortable: true },
    { key: "physician.name", label: "Referred To", sortable: true },
    {
      label: "Unit - District",
      key: "unit",
      sortable: true,
      render: (row) => (
        <div className="text-xs whitespace-nowrap">
          <div>
            <span className="bg-yellow-200 text-end text-black-700 px-2 mt-3 rounded-full text-[10px]">
              {row?.unit?.name} - {row?.unit?.district?.name}
            </span>
          </div>
        </div>
      ),
    },
    { key: "hospital_name", label: "Hospital", sortable: true },
    { key: "referred_location", label: "Referred Location", sortable: true },
    {
      key: "status",
      label: "Status",
      type: "status",
    },
  ];

  const renderActions = (row) => (
    <div className="flex gap-2">
      {!trashed ? (
        <Button
          onClick={() => navigate(ROUTE_PATHS.REFFERAL_CREATE, { state: { patient_referral: row } })}
          title="Edit"
          icon={Edit3Icon}
          className="text-green-600 hover:bg-green-100 hover:text-green-700 dark:hover:bg-green-500/10"
        />
      ) : (
        <Button
          onClick={() => handleSingle(row, "restore")}
          title="Restore"
          icon={RotateCcw}
          className="text-green-600 hover:bg-green-100 hover:text-green-700 dark:hover:bg-green-500/10"
        />
      )}
      <Button
        onClick={() => handleSingle(row, trashed ? "destroy" : "delete")}
        title="Delete"
        icon={TrashIcon}
        className="text-red-600 hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-500/10"
      />
    </div>
  );

  return (
    <>
      <BreadCrumb title="Refferal List " />
      <TableListing
        headers={headers}
        paginationData={patientReferrals}
        loading={isFetching}
        createButton
        createLink={ROUTE_PATHS.REFFERAL_CREATE}
        actions={renderActions}
        handleBulk={handleMultiple}
        search={search}
        setSearch={setSearch}
        trashed={trashed}
        setTrashed={setTrashed}
        onTrashedChange={handleTrashedChange}
        onPageChange={setPage}
        statusModule={"patient_referral"}
        onRowsPerPageChange={(value) => {
          setLimit(value);
          resetPage();
        }}
        onSearch={(value) => {
          setSearch(value);
          resetPage();
        }}
        onSortChange={(sort) => {
          setSortConfig(sort);
          resetPage();
        }}
      />
    </>
  );
};

export default PatientReferralList;
